<template>
  <div class="case-logic-browser">
    <md-field class="search-field">
      <md-input
        placeholder="Search"
        :value="state.searchTerm"
        @input="updateSearchTerm"
      />
    </md-field>
    <md-field class="filter-field">
      <md-select
        ref="filterSelect"
        v-model="state.filteredCategory"
        placeholder="Filter"
        @md-closed="blurFilterSelect"
      >
        <md-option>Clear</md-option>
        <md-option value="Action">Actions</md-option>
        <md-option value="Check">Checks</md-option>
        <md-option value="Effect">Effects</md-option>
        <md-option value="Timer">Timers</md-option>
        <md-option value="EffectChain">Effect Chains</md-option>
        <md-option
          v-for="category in allCategories"
          :key="category"
          :value="category"
        >
          {{ category }}
        </md-option>
      </md-select>
    </md-field>
    <md-list class="controls-list md-scrollbar md-theme-default">
      <!-- Nodes first when searching -->
      <template v-if="state.searchTerm">
        <md-list-item
          v-for="control in rootOrderedCaseLogicControls"
          :key="control.key"
          class="control-item"
          @click="onSelect($event, { nodeId: control.node.id })"
        >
          <span class="md-list-item-text">
            {{ control.label }} {{ startCase(control.node.name) }}
          </span>
        </md-list-item>
      </template>
      <md-list-item
        v-for="control in groupOrderedCaseLogicControls"
        :key="control.key"
        md-expand
        class="control-item"
        @click="onSelect($event, { nodeId: control.node.id })"
      >
        <md-icon>folder</md-icon>
        <span class="md-list-item-text">
          {{ control.label }}
        </span>
        <md-list slot="md-expand" class="controls-list">
          <md-list-item 
            v-for="child in control.children"
            :key="child.key"
            class="control-item md-inset"
            @click="onSelect($event, { nodeId: child.node.id })"
          >
            <span class="md-list-item-text">
              {{ child.label }} {{ startCase(child.node.name) }}
            </span>
          </md-list-item>
        </md-list>
      </md-list-item>
      <!-- Groups first when not searching -->
      <template v-if="!state.searchTerm">
        <md-list-item
          v-for="control in rootOrderedCaseLogicControls"
          :key="control.key"
          class="control-item"
          @click="onSelect($event, { nodeId: control.node.id })"
        >
          <span class="md-list-item-text">
            {{ control.label }} {{ startCase(control.node.name) }}
          </span>
        </md-list-item>
      </template>
    </md-list>
  </div>
</template>

<script>
import debounce from 'lodash/debounce';
import upperFirst from 'lodash/upperFirst';
import flatten from 'lodash/flatten';
import uniq from 'lodash/uniq';
import map from 'lodash/map';
import groupBy from 'lodash/groupBy';
import fromPairs from 'lodash/fromPairs';
import concat from 'lodash/concat';
import toArray from 'lodash/toArray';
import orderBy from 'lodash/orderBy';
import startCase from 'lodash/startCase';
import $ from 'jquery';
import Rete from 'rete';

import ACTION_CONTROLS from '../ACTION_CONTROLS';
import CHECK_CONTROLS from '../CHECK_CONTROLS';
import EFFECT_CONTROLS from '../EFFECT_CONTROLS';
import EFFECT_CHAIN_CONTROLS from '../EFFECT_CHAIN_CONTROLS';
import { getControlDescriptionForType } from '../utils/getControlForType';

const CATEGORY_ORDER = [
  'Scenario',
  'Transport',
  'Patient Conditions',
  'Questions',
  'IV',
  'Medications',
  'Oxygen',
  'Procedures',
  'Monitors',
  'Media',
  'Grading',
  'Custom States',
];
export default {
  name: 'case-logic-browser',
  props: {
    caseLogicDescription: {
      type: Object,
      required: true,
    },
    caseLogicEditor: {
      type: Rete.NodeEditor,
      required: true,
    },
  },
  data() {
    const allCategories = orderBy(
      uniq(
        concat(
          toArray(ACTION_CONTROLS),
          toArray(CHECK_CONTROLS),
          toArray(EFFECT_CONTROLS),
          toArray(EFFECT_CHAIN_CONTROLS)
        ).map((controlDescription) => controlDescription.category)
      ),
      (category) =>
        CATEGORY_ORDER.indexOf(category) !== -1
          ? CATEGORY_ORDER.indexOf(category)
          : Infinity
    ).filter((category) => !!category);
    return {
      state: {
        searchTerm: '',
        filteredNodeIds: null,
        expansionStates: fromPairs(
          map(allCategories, (category) => [category, true])
        ),
        filteredCategory: null,
      },
      allCategories,
    };
  },
  computed: {
    caseLogicControlsByCategory() {
      return groupBy(this.caseLogicControls, 'category');
    },
    rootOrderedCaseLogicControls() {
      return orderBy(
        this.caseLogicControls.filter(ctrl => !ctrl.groupId),
        (control) => control.node.name + control.label
      );
    },
    groupOrderedCaseLogicControls() {
      const groupControls = [];
      const ctrlByGroup = groupBy(this.caseLogicControls, 'groupId');
      Object.entries(ctrlByGroup).forEach(([key, value]) => {
        if (key !== 'undefined') {
          const node = this.caseLogicDescription.nodes[key];
          groupControls.push({
            label: node.data.title,
            node,
            category: 'Group',
            children: orderBy(value, (control) => control.node.node + control.label),
          });
        }
      });
      return orderBy(groupControls, (control) => control.label);
    },
    caseLogicControls() {
      if (!this.caseLogicDescription) {
        return [];
      }
      const filteredNodes = this.state.filteredNodeIds
        ? this.state.filteredNodeIds.map(
            (nodeId) => this.caseLogicDescription.nodes[nodeId]
          )
        : this.caseLogicDescription.nodes;

      const filteredControls = flatten(
        map(filteredNodes).map((node) => {
          if (node.name === 'Action' && node.data.action) {
            const controlDescription = getControlDescriptionForType(
              node.data.action.type,
              node.data.action,
              ACTION_CONTROLS
            );
            return {
              label: controlDescription.title,
              key: node.id,
              node,
              groupId: this.nodeToGroupMap[node.id],
              category: controlDescription.category,
            };
          }
          if (node.name === 'Check' && node.data) {
            return map(node.data, (check, checkKey) => {
              const controlDescription = getControlDescriptionForType(
                check.type,
                check,
                CHECK_CONTROLS
              );
              return {
                label: controlDescription.title,
                key: `${node.id}-${checkKey}`,
                node,
                groupId: this.nodeToGroupMap[node.id],
                check,
                category: controlDescription.category,
              };
            });
          }
          if (node.name === 'Effect' && node.data.effect) {
            const controlDescription = getControlDescriptionForType(
              node.data.effect.type,
              node.data.effect,
              EFFECT_CONTROLS
            );
            return {
              label: controlDescription.title,
              key: node.id,
              node,
              groupId: this.nodeToGroupMap[node.id],
              category: controlDescription.category,
            };
          }
          if (node.name === 'Timer') {
            return {
              label: '',
              key: node.id,
              node,
              groupId: this.nodeToGroupMap[node.id],
              category: 'Timers',
            };
          }
          if (node.name === 'EffectChain') {
            return {
              label: '',
              key: node.id,
              node,
              groupId: this.nodeToGroupMap[node.id],
              category: 'Effect Chains',
            };
          }
        })
      )
        .filter((control) => !!control)
        .filter((control) => {
          if (!this.state.filteredCategory) {
            return true;
          }
          if (control.node.name === this.state.filteredCategory) {
            return true;
          }
          if (control.category === this.state.filteredCategory) {
            return true;
          }
          return false;
        });

      return filteredControls;
    },
    nodeToGroupMap() {
      const nodeToGroupMap = {};
      Object.values(this.caseLogicDescription.nodes).forEach(node => {
        if (node.name === 'Group' && node.data.childIds) {
          node.data.childIds.forEach(childId => {
            nodeToGroupMap[childId] = node.id;
          });
        }
      });
      return nodeToGroupMap;
    },
    controlTitlesByNodeAndType() {
      return {
        Action: this.getControlTitlesByType(ACTION_CONTROLS),
        Check: this.getControlTitlesByType(CHECK_CONTROLS),
        Effect: this.getControlTitlesByType(EFFECT_CONTROLS),
        EffectChain: this.getControlTitlesByType(EFFECT_CHAIN_CONTROLS),
      };
    },
    nodesById() {
      const nodeIdsByElMap = new Map();
      this.caseLogicEditor.nodes.forEach((node) => {
        nodeIdsByElMap.set(node.id, node);
      });
      return nodeIdsByElMap;
    },
    nodeIdsByEl() {
      return new Map(
        Array.from(this.nodesById, ([id, node]) => [node._vue.$el, id])
      );
    },
  },
  watch: {
    'state.filteredCategory'(filteredCategory) {
      if (filteredCategory) {
        this.highlightFilteredNodes();
      } else {
        this.clearHighlightedNodes();
      }
    },
  },
  mounted() {
    this.caseLogicEditor.on('nodeselected', (node) => {
      if (node.name === 'Frame') {
        return;
      }
      this.state.filteredCategory = null;
      this.clearHighlightedNodes();
    });
    this.caseLogicEditor.on('nodecreated', () => {
      if (this.state.filteredCategory) {
        this.$nextTick(() => {
          this.highlightFilteredNodes();
        })
      }
    })
  },
  methods: {
    updateSearchTerm(term) {
      this.state.searchTerm = term;
      this.search();
    },
    search: debounce(function search() {
      const { searchTerm } = this.state;
      if (!searchTerm || searchTerm.length < 2) {
        this.state.filteredNodeIds = null;
        return;
      }
      const searchTermVariants = [
        searchTerm,
        searchTerm.toUpperCase(),
        searchTerm.toLowerCase(),
        upperFirst(searchTerm),
      ];
      const matchingElements = uniq(
        flatten(
          searchTermVariants.map((searchTermVariant) => {
            const matchingTextElements = $(this.caseLogicEditor.view.container)
              .find(
                `.node span:visible:contains('${searchTermVariant}'),.node button:visible:contains('${searchTermVariant}')`
              )
              .toArray();
            const matchingInputs = $(this.caseLogicEditor.view.container)
              .find('input')
              .toArray()
              .filter((input) => input.value.includes(searchTermVariant));
            return [...matchingTextElements, ...matchingInputs];
          })
        )
      );
      const resultNodes = uniq(
        matchingElements.map((element) => $(element).closest('.node')[0])
      ).filter((v) => !!v);
      this.state.filteredNodeIds = resultNodes.map((node) =>
        this.nodeIdsByEl.get(node)
      );
    }, 500),
    onSelect(event, { nodeId }) {
      event.stopPropagation();
      const node = this.nodesById.get(nodeId);
      this.$emit('onSelect', { node });
    },
    highlightFilteredNodes() {
      this.clearHighlightedNodes();
      const nodeIdsToHighlight = this.caseLogicControls
        .map(control => control.node.id)
        // Need to do this for nodes that dont have controls
        // yet (e.g. an empty check node)
        .concat(this.caseLogicEditor.nodes.filter(node => node.name === this.state.filteredCategory).map(node => node.id));
      this.caseLogicEditor.nodes.forEach(node => {
        if (nodeIdsToHighlight.includes(node.id)) {
          $(node._vue.$el).addClass('highlighted');
        }
      });
    },
    clearHighlightedNodes() {
      $(this.caseLogicEditor.view.container).find('.node').removeClass('highlighted');
    },
    blurFilterSelect() {
      this.$nextTick(() => {
        $(this.$refs.filterSelect.$el).find('.md-input').blur();
      });
    },
    startCase,
  },
};
</script>

<style scoped>
.case-logic-browser {
  display: flex;
  flex-direction: column;
}
.search-field, .filter-field {
  padding: 10px;
}
.controls-list {
  flex: 1;
  overflow: auto;
  background: inherit;
  color: inherit;
  padding: 10px;
}
.controls-list::-webkit-scrollbar-thumb {
  border-radius: 0;
}
.control-item >>> .md-list-item-container,
.control-item >>> .md-list-item-container:hover {
  color: inherit !important;
}
.control-item >>> .md-list-item-content:hover {
  color: inherit !important;
}
.control-item >>> .md-icon {
  color: inherit !important;
}
.control-item >>> .md-icon.md-theme-default.md-icon-image svg {
  fill: white !important;
}
ul .md-list.controls-list {
  padding: 0;
}
</style>